import { useState, useEffect, useMemo, useContext } from "react";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";

// Soft UI Dashboard PRO React example components
import DataTable from "cxfcomponents/Tables/DataTable";
import InviteModal from "./components/InviteModal";
import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";

import "react-toastify/dist/ReactToastify.css";
import { UsersStatusContext } from "context/AppProvider/UsersStatusContext";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import { useUsers } from "hooks/useUsers";
import { useRoles } from "hooks/useRoles";
import Tiles from "./components/tiles";
import UserActions from "./components/Reviews/ActionCellfromUser";
import TransferOwnershipDialog from "./components/Reviews/TransferOwnershipDialog";

function Reports1() {
   const [openInviteModal, setOpenInviteModal] = useState(false);
   const [options, setOptions] = useState({
      roles: [],
      status: [
         { value: "active", label: "Active" },
         { value: "invited", label: "Invited" },
         { value: "suspended", label: "Suspended" }
      ]
   });
   const [transferTarget, setTransferTarget] = useState(null);
   const { usersStatus } = useContext(UsersStatusContext);
   const { roles } = useRoles(false);
   const { users, handleFetchUsers } = useUsers(false);
   const [tableView, setTableView] = useState("list");

   const reloadUsersList = () => {
      handleFetchUsers();
   };

   const handleCloseTransferDialog = () => setTransferTarget(null);

   const checkUserStatus = (auth0_id) => {
      if (usersStatus) {
         const userStatus = usersStatus.find((item) => item.auth0_id === auth0_id)?.status;
         return userStatus;
      }
   };


   const openTransferDialog = (username, auth0Id) => setTransferTarget({ username, auth0Id });


   const dataTableData = useMemo(() => {
      if (users.length && roles.length && options.roles.length !== 0) {
         return {
            columns: [
               {
                  Header: "users",
                  accessor: "users",
                  disableSortBy: false,
                  defaultSortOrder: "asc",
                  canGroupBy: false,
                  width: 500,
                  Cell: ({ value }) => {
                     return (
                        <ProductCellfromUser
                           id={value.item.auth0_id}
                           image={value.item.image}
                           name={`${value.item.first_name} ${value.item.last_name}`}
                           firstName={value.item.first_name}
                           lastName={value.item.last_name}
                           email={value.item.email_address}
                           checked={true}
                           status={value.status}
                           userStatus={value.item.status}
                        />
                     );
                  },
                  sortType: (rowA, rowB, columnId) => {
                     const nameA = rowA.values[columnId].name;
                     const nameB = rowB.values[columnId].name;
                     return nameA.localeCompare(nameB);
                  }
               },
               {
                  Header: "name",
                  accessor: "name",
                  show: false
               },
               {
                  Header: "role",
                  accessor: "role",
                  aggregate: "count",
                  Aggregated: ({ value }) => `${value} roles`,
                  canGroupBy: false,
                  Cell: ({ value }) => (
                     <SoftTypography fontSize="14px" fontWeight="bold">
                        {value}
                     </SoftTypography>
                  )
               },
               {
                  Header: "status",
                  accessor: "status",
                  Cell: ({ value }) => {
                     return <StatusBadge status={value} />;
                  },
                  canGroupBy: false
               },
               { Header: "actions", accessor: "action", disableSortBy: true, canGroupBy: false }
            ].map((column) => ({ ...column, disableResize: true })),
            rows: users.length
               ? [
                    ...users.map((item) => {
                       const status = checkUserStatus(item.auth0_id);

                       return {
                          users: {
                             item: item,
                             status: status,
                             name: `${item.first_name} ${item.last_name}`
                          },

                          name: `${item.first_name} ${item.last_name}`,
                          role: (() => {
                             if (roles.length !== 0) {
                                if (!item.role_id) {
                                   return "";
                                } else {
                                   const roleName = roles.find((role) => role.id === item.role_id)?.name;
                                   return roleName ? roleName : "";
                                }
                             } else {
                                return "";
                             }
                          })(),

                          status: item.status,
                          action: <UserActions
                             auth0Id={item.auth0_id}
                             tenantId={item.tenant_id}
                             currentUser={item}
                             status={item.status}
                             roleName={roles.find((role) => role.id === item.role_id)?.name}
                             getUsers={handleFetchUsers}
                             username={`${item.first_name || ""} ${item.last_name || ""}`.trim() || item.email_address}
                             openTransferDialog={openTransferDialog}
                          />
                       };
                    })
                 ]
               : []
         };
      }
   }, [users, roles, options.roles, usersStatus]);

   const isDataLoaded = users.length !== 0 && roles.length !== 0 && !!dataTableData;

   useEffect(() => {
      if (!roles.length) return;

      setOptions((prev) => ({
         ...prev,
         roles: [{ value: "", label: "All" }, ...roles.map((role) => ({ value: role.name, label: role.name }))]
      }));
   }, [roles]);

   return (
      <>
         <div
            style={{
               padding: "8px",
               position: "sticky",
               top: 0,
               zIndex: 1000,
               backgroundColor: "var(--main-bg-color)"
            }}
         >
            <DashboardNavbar />
         </div>
         <DashboardLayout>
            {isDataLoaded && (
               <SoftBox mt={-3.1} mb={0.5}>
                  <Card
                     sx={{
                        overflow: "visible",
                        boxShadow: 0,
                        backgroundColor: "transparent !important"
                     }}
                  >
                     <DataTable
                        options={options}
                        table={dataTableData}
                        splitted={true}
                        setOpenInviteModal={setOpenInviteModal}
                        reloadUsersList={reloadUsersList}
                        withStatusFilter={true}
                        withRoleFilter={true}
                        withSearchbar={true}
                        tableView={tableView}
                        setTableView={setTableView}
                        withTileview
                        withUsersOptions
                        tileViewComponent={(rows) => <Tiles rows={rows} />}
                        tableHeight="clamp(400px, calc(100vh - 87px), calc(100vh - 87px))"
                     />
                     <InviteModal roles={roles} reloadUsersList={reloadUsersList} openInviteModal={openInviteModal} setOpenInviteModal={setOpenInviteModal} users={users} />
                  </Card>
               </SoftBox>
            )}
            <TransferOwnershipDialog
               open={Boolean(transferTarget)}
               onClose={handleCloseTransferDialog}
               onUsersChanged={handleFetchUsers}
               targetUser={transferTarget}
            />
         </DashboardLayout>
      </>
   );
}

export default Reports1;
